import {
  CommandBar,
  DetailsListLayoutMode,
  FontIcon,
  IColumn,
  ICommandBarItemProps,
  mergeStyleSets,
  SelectionMode,
  ShimmeredDetailsList,
  Stack,
} from '@fluentui/react';
import { useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { appRoles } from '../../authConfig';
import { scrollStackItemStyles } from '../../common/styles/StackStyles';
import {
  getAssetStatuses,
  IAssetStatus,
  IStatus,
} from '../../services/assetServices';
import {
  getOrderStatuses,
  getWorkStatuses,
  IOrderStatus,
  IWorkStatus,
} from '../../services/workService';
import AppContext from '../AppContext';
import { useAtomValue, useSetAtom } from 'jotai';
import {
  errorMessageAtom,
  isInProgressAtom,
} from '../../atoms/messageBarAtoms';
import { profileDataAtom } from '../../atoms/authAtoms';
import { Selection } from '@fluentui/react/lib/DetailsList';

export interface IStatuses {
  entityType: string;
}

const Statuses = (props: IStatuses) => {
  const context = useContext(AppContext);
  const [selectedStatus, setSelectedStatus] = useState<IStatus>();
  const setErrorMessage = useSetAtom(errorMessageAtom);
  const setIsInProgress = useSetAtom(isInProgressAtom);
  const profileData = useAtomValue(profileDataAtom);

  const navigate = useNavigate();

  const [shimmered, setShimmered] = useState(false);
  const [workStatuses, setWorkStatuses] = useState<IWorkStatus[]>([]);
  const [assetStatuses, setAssetStatuses] = useState<IAssetStatus[]>([]);
  const [orderStatuses, setOrderStatuses] = useState<IOrderStatus[]>([]);

  const fetchData = async () => {
    setIsInProgress(true);
    const abortController = new AbortController();
    try {
      if (props.entityType === 'work') {
        context.setSelectedTab('Work Statuses');
        const data: IWorkStatus[] = await getWorkStatuses(abortController);

        setWorkStatuses(
          data.map((status) => ({
            ...status,
            isCompleted: status.isCompleted ?? status.IsCompleted,
          }))
        );
        setShimmered(false);
      } else if (props.entityType === 'asset') {
        context.setSelectedTab('Asset Statuses');

        const data: IAssetStatus[] = await getAssetStatuses(abortController);

        setAssetStatuses(data);
        setShimmered(false);
      } else if (props.entityType === 'order') {
        context.setSelectedTab('Order Statuses');

        const data: IOrderStatus[] = await getOrderStatuses(abortController);

        setOrderStatuses(
          data.map((status) => ({
            ...status,
            isCompleted: status.isCompleted ?? status.IsCompleted,
          }))
        );
        setShimmered(false);
      }
    } catch (error: any) {
      console.error('Error:', error);
      setErrorMessage(error.message);
    } finally {
      setIsInProgress(false);
    }

    return () => {
      abortController.abort();
    };
  };

  useEffect(() => {
    fetchData();
  }, [props.entityType]);

  const baseColumns: IColumn[] = [
    {
      key: 'Id',
      name: 'Id',
      fieldName: 'id',
      minWidth: 25,
      maxWidth: 50,
      isResizable: true,
    },
    {
      key: 'name',
      name: 'Name',
      fieldName: 'name',
      minWidth: 250,
      maxWidth: 250,
      isResizable: true,
    },
    {
      key: 'code',
      name: 'Code',
      fieldName: 'code',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'icon',
      name: 'Icon',
      fieldName: 'icon',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: 'color',
      name: 'Color',
      fieldName: 'color',
      minWidth: 70,
      maxWidth: 100,
      isResizable: true,
    },
  ];

  const _columns: IColumn[] =
    props.entityType === 'work' || props.entityType === 'order'
      ? [
          ...baseColumns,
          {
            key: 'clientLabel',
            name: 'Client Label',
            fieldName: 'clientLabel',
            minWidth: 150,
            maxWidth: 220,
            isResizable: true,
          },
          {
            key: 'managerLabel',
            name: 'Manager Label',
            fieldName: 'managerLabel',
            minWidth: 150,
            maxWidth: 220,
            isResizable: true,
          },
          {
            key: 'isCompleted',
            name: 'Is Completed',
            fieldName: 'isCompleted',
            minWidth: 90,
            maxWidth: 120,
            isResizable: true,
          },
        ]
      : baseColumns;

  const workCellClass = mergeStyleSets({
    default: {
      padding: '5px',
    },
    bar: {
      height: '24px',
      display: 'block',
    },
  });

  const _renderItemColumn = (
    item: IWorkStatus,
    _index?: number,
    column?: IColumn
  ) => {
    const fieldContent = item[column?.fieldName as keyof IWorkStatus] as
      | string
      | boolean
      | undefined;

    switch (column?.key) {
      case 'color':
        return item.color ? (
          <span
            className={workCellClass.bar}
            style={{ backgroundColor: `${item.color}` }}
          ></span>
        ) : (
          <span></span>
        );
      case 'icon':
        return item.icon ? (
          <FontIcon
            iconName={item?.icon}
            style={{ width: 25, height: 25, fontSize: 25 }}
          />
        ) : (
          <span></span>
        );
      case 'isCompleted':
        return <span>{fieldContent ? 'Yes' : 'No'}</span>;
      default:
        return <span>{fieldContent}</span>;
    }
  };

  const _items: ICommandBarItemProps[] = [
    {
      key: 'newStatus',
      text: 'New',
      iconProps: { iconName: 'Add' },
      disabled: !profileData.roles.includes(appRoles.Admin),
      onClick: () => {
        navigate('/newStatuses/' + props.entityType);
      },
    },
    {
      key: 'editStatuse',
      text: 'Edit',
      iconProps: { iconName: 'Edit' },
      disabled: !profileData.roles.includes(appRoles.Admin),
      onClick: () => {
        if (selectedStatus) {
          navigate(`/editStatuses/${props.entityType}?id=${selectedStatus.id}`);
        }
      },
    },
  ];

  const _selection = new Selection({
    onSelectionChanged: () => {
      const sel = _getSelectionDetails();
      if (sel.item) {
        setSelectedStatus(sel.item);
      }
    },
  });

  const _getSelectionDetails = () => {
    const selectionCount = _selection.getSelectedCount();

    switch (selectionCount) {
      case 0:
        return { count: 0, message: 'No items selected' };
      case 1: {
        const firstSelection = _selection.getSelection()[0] as IStatus;
        return {
          count: 1,
          id: firstSelection.id,
          item: firstSelection,
        };
      }
      default:
        return {
          count: selectionCount,
          message: `${selectionCount} items selected`,
        };
    }
  };

  return (
    <>
      <Stack styles={{ root: { height: 450, width: 800 } }}>
        <CommandBar
          items={_items}
          ariaLabel="Items actions"
          primaryGroupAriaLabel="Items actions"
          farItemsGroupAriaLabel="More actions"
        />
        <Stack>
          <Stack.Item styles={scrollStackItemStyles}>
            <ShimmeredDetailsList
              items={
                props.entityType === 'work'
                  ? workStatuses
                  : props.entityType === 'order'
                  ? orderStatuses
                  : assetStatuses
              }
              columns={_columns}
              setKey="set"
              layoutMode={DetailsListLayoutMode.justified}
              onRenderItemColumn={_renderItemColumn}
              selectionMode={SelectionMode.single}
              selectionPreservedOnEmptyClick={true}
              selection={_selection}
              ariaLabelForSelectionColumn="Toggle selection"
              ariaLabelForSelectAllCheckbox="Toggle selection for all items"
              checkButtonAriaLabel="select row"
              enableShimmer={shimmered}
            />
          </Stack.Item>
        </Stack>
      </Stack>
    </>
  );
};

export default Statuses;
